import Vuex from 'vuex';
import Vue from 'vue';
import axios from 'axios';
import {
    ADDRBOOK_LOCATION,
    APP_MAIN_EXCHANGE_CURRENCY,
    APP_MAIN_LOCALE,
    APP_MAIN_THEME,
    APP_MAIN_TX_SOURCE,
} from '~/config.js';
import { Address } from '~/tonweb.js';
import { getExchangeRateAndTrend } from '~/api/coingecko.js';
import { getFallbackExchangeRate, rememberExchangeRate } from '~/exchangeRate.js';

Vue.use(Vuex);

const saveToLs = function saveValueToLocalStorage(key, value) {
    window.localStorage.setItem(key, JSON.stringify(value));
};

const toRawAddress = (address) => {
    try {
        return Address.parse(address).toString({ userFriendly: false });
    } catch (e) {
        return address;
    }
};

const getFromLs = (key, defaultValue = null) => {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
};

// migration from old format, remove after some time and replace with _schema_version_:
const getRecentSearchesFromLs = () => {
    const searches = getFromLs('searchRecentAddresses', []);

    searches.forEach((item) => {
        if (!item.key) {
            item.key = `recent_${item.address}`;
            item.address_type = 'address';
            item.image = null;
            item.routeParams = { address: item.address };
        }
    });

    return searches.map(Object.freeze);
};

/* eslint no-param-reassign: "off" */
export default new Vuex.Store({
    state: {
        addrbook: {},
        timeFormatData: window.localStorage.getItem('timeFormatData') || 'smart',
        // The «alternative» view is gone: whoever still has it saved gets the default one.
        transactionViewData: window.localStorage.getItem('transactionViewData') === 'professional' ? 'professional' : 'simple',
        exchangeRate: 0,
        exchangeRateTrend: 0,
        filterShow: false,
        dyorLinkShow: false,
        nftItemCountShow: false,
        nftFilterShow: false,
        nftSpamShown: false,
        nftSpamCount: 0,
        // What's typed into the loupe in the NFT tab header, read by the grid:
        nftSearchQuery: '',
        // Set to false while a fullscreen filter/calendar menu is open, so that
        // the "scroll to top" button doesn't overlap it:
        filterVisible: true,
        exchangeRateCurrency: window.localStorage.getItem('exchangeRateCurrency') || APP_MAIN_EXCHANGE_CURRENCY,
        appLocale: window.localStorage.getItem('appLocale') || APP_MAIN_LOCALE,
        appLocalePreferenceSet: window.localStorage.getItem('appLocale') !== null,
        appLocalAddressbook: getFromLs('localAddressbook') || [],
        presaveAddressbookItem: {},
        appTheme: window.localStorage.getItem('appTheme') || APP_MAIN_THEME,
        appIsScrolled: false,
        searchRecentAddresses: getRecentSearchesFromLs(),
        txTableSource: window.localStorage.getItem('txTableSource') || APP_MAIN_TX_SOURCE, // Temporary set default to tonapi (APP_MAIN_TX_SOURCE)
        actionTypeFilter: window.localStorage.getItem('actionTypeFilter') || false,
        datePickerFilter: [undefined, undefined],
        newDefaultTxView: window.localStorage.getItem('newDefaultTxView') || false,
        closeAdTill: Number(window.localStorage.getItem('closeAdTill')) || 0,
        actionGroups: {
            contractCall: ['call_contract', 'contract_deploy', 'multisig_execute', 'gasless_request', 'unknown'],
            gramTransfer: ['ton_transfer'],
            exchange: ['jetton_swap'],
            jettonTransfer: ['jetton_transfer'],
            nftTransfer: ['nft_transfer', 'nft_purchase'],
            staking: ['stake_withdrawal', 'stake_withdrawal_request', 'stake_deposit', 'jvault_stake', 'jvault_unstake', 'jvault_claim', 'jvault_unstake_request'],
            bids: ['auction_bid'],
            auctions: ['teleitem_start_auction', 'teleitem_cancel_auction'],
            dns: ['renew_dns', 'change_dns', 'delete_dns'],
            liquidity: ['dex_deposit_liquidity', 'dex_withdraw_liquidity'],
            mint: ['nft_mint', 'jetton_mint'],
            burn: ['jetton_burn'],
            lending: ['evaa_supply', 'evaa_withdraw', 'evaa_liquidate'],
            multisig: ['multisig_create_order', 'multisig_approve', 'multisig_execute'],
            subscription: ['subscribe', 'unsubscribe'],
            vesting: ['vesting_send_message', 'vesting_add_whitelist'],
            elections: ['election_recover', 'election_deposit'],
            keyChange: ['change_wallet_key'],
        },
        excludeGroups: getFromLs('excludeActionGroups') || [],
        openTxDetails: window.localStorage.getItem('openTxDetails') === 'true',
        showTxComments: window.localStorage.getItem('showTxComments') === 'true',
        transactionDirection: window.localStorage.getItem('transactionDirection') || 'all',
        // «Initiated by this address only» is not persisted: the value belongs to the address it was
        // switched on, so it resets on every other address.
        initiatedByAccount: false,
        initiatedByAccountAddress: null,
        websocketAccountState: null,
        websocketTransactions: [],
        websocketPendingTransactions: [],
        websocketAddressAssets: {},
        highlightedNodeName: null,
    },

    mutations: {
        updateFilterShow(state, payload) {
            state.filterShow = payload;
        },

        updateDyorLinkShow(state, payload) {
            state.dyorLinkShow = payload;
        },

        updateNftItemCountShow(state, payload) {
            state.nftItemCountShow = payload;
        },

        updateNftFilterShow(state, payload) {
            state.nftFilterShow = payload;
        },

        updateNftSpamShown(state, payload) {
            state.nftSpamShown = payload;
        },

        updateNftSpamCount(state, payload) {
            state.nftSpamCount = payload;
        },

        updateNftSearchQuery(state, payload) {
            state.nftSearchQuery = payload;
        },

        updateFilterVisible(state, payload) {
            state.filterVisible = payload;
        },

        updateTimeFormatData(state, payload) {
            state.timeFormatData = payload;
        },

        updateTransactionViewData(state, payload) {
            state.transactionViewData = payload;
        },

        updateTransactionDirection(state, payload) {
            state.transactionDirection = payload;
            window.localStorage.setItem('transactionDirection', payload);
        },

        updateInitiatedByAccount(state, { address, value }) {
            state.initiatedByAccountAddress = toRawAddress(address);
            state.initiatedByAccount = value;
        },

        updateLocale(state, { locale, forget = false }) {
            state.appLocale = locale;
            state.appLocalePreferenceSet = true;
            if (!forget) {
                window.localStorage.setItem('appLocale', locale);
            }
        },

        updateExchangeRateCurrency(state, currency) {
            state.exchangeRateCurrency = currency;
            window.localStorage.setItem('exchangeRateCurrency', currency);
        },

        updateTheme(state, theme) {
            state.appTheme = theme;
            window.localStorage.setItem('appTheme', theme);
        },

        updateScrollState(state, isScrolled) {
            state.appIsScrolled = isScrolled;
        },

        updateTxTableSource(state, source) {
            state.txTableSource = source;
            window.localStorage.setItem('txTableSource', source);
        },

        updateRecentSearch(state, { suggestion, forget }) {
            const routes = state.searchRecentAddresses;

            const idx = routes.findIndex((route) => {
                // older entries don't have key:
                const key = route.key ?? route.address;
                return key === suggestion.key;
            });

            // if we already have this address, remove it,
            // since it'll either way be added to the top:
            if (idx >= 0) {
                routes.splice(idx, 1);
            }

            // store only limited set of records:
            if (routes.length >= 8) {
                routes.splice(7, routes.length);
            }

            // push new item to the top of the list:
            if (!forget) {
                routes.unshift(Object.freeze(suggestion));
            } else {
                // if forgetting the item, just do nothing
            }

            saveToLs('searchRecentAddresses', routes);
        },

        updateAddrbook(state, data) {
            const addrbook = {};
            const defaultInfo = {
                isScam: false,
                name: undefined,
            };

            Object.entries(data).forEach(([address, mixedData]) => {
                const addressInfo = typeof mixedData === 'string'
                    ? { ...defaultInfo, name: mixedData }
                    : { ...defaultInfo, ...mixedData };

                addrbook[address] = Object.freeze(addressInfo);
            });

            state.addrbook = addrbook;
        },

        /**
         * @param  {Object} state
         * @param  {Number} options.rate
         * @param  {Number} options.trend
         * @return {undefined}
         */
        updateExchangeRate(state, { rate, trend }) {
            state.exchangeRate = rate;
            state.exchangeRateTrend = trend;
        },

        addLocalAddressbook(state) {
            const currentAddressbook = getFromLs('localAddressbook');
            const presavedItem = state.presaveAddressbookItem;

            if (presavedItem?.address === undefined) return;

            if (currentAddressbook && currentAddressbook.length > 0) {
                const isAlreadyInAb = currentAddressbook.find(i => i.address === presavedItem.address);

                if (!isAlreadyInAb) {
                    const untitledAddresses = currentAddressbook.filter(item => item.name > 0);
                    const itemName = untitledAddresses.length > 0
                        ? untitledAddresses.sort((a, b) => b.name - a.name)[0].name
                        : 0;
                    const newItem = {
                        address: presavedItem.address,
                        name: presavedItem?.name || itemName + 1,
                        type: presavedItem?.type || 'address',
                        image: presavedItem?.image,
                    };

                    currentAddressbook.unshift(newItem);
                    state.appLocalAddressbook = currentAddressbook;
                    saveToLs('localAddressbook', currentAddressbook);
                }
            } else {
                const newItem = {
                    address: presavedItem.address,
                    name: presavedItem?.name || 1,
                    type: presavedItem?.type || 'address',
                    image: presavedItem?.image,
                };

                const newAddressbook = [newItem];
                state.appLocalAddressbook = newAddressbook;
                saveToLs('localAddressbook', newAddressbook);
            }
        },

        removeLocalAddressbook(state, { address }) {
            const currentAddressbook = getFromLs('localAddressbook');

            const idx = currentAddressbook.findIndex(item => item.address === address);

            if (idx >= 0) {
                currentAddressbook.splice(idx, 1);
            }

            state.appLocalAddressbook = currentAddressbook;
            saveToLs('localAddressbook', currentAddressbook);
        },

        updateLocalAddressbook(state, { address, newName }) {
            const currentAddressbook = getFromLs('localAddressbook');

            const idx = currentAddressbook.findIndex(item => item.address === address);

            if (idx >= 0 && newName.length > 0) {
                currentAddressbook[idx].name = newName.substring(0, 32);

                state.appLocalAddressbook = currentAddressbook;
                saveToLs('localAddressbook', currentAddressbook);
            }
        },

        presaveLocalAddressbook(state, { address, name, type, image }) {
            state.presaveAddressbookItem = {
                address,
                name,
                type,
                image,
            };
        },

        adCloseBannerAction(state) {
            const now = Math.round(Date.now() / 1000);
            const closePeriod = 24 * 60 * 60;
            const closeTill = now + closePeriod;

            state.closeAdTill = closeTill;
            window.localStorage.setItem('closeAdTill', String(closeTill));
        },

        setDatePickerFilter(state, date) {
            state.datePickerFilter = date;
        },

        resetDatePickerFilter(state) {
            state.datePickerFilter = [undefined, undefined];
        },

        switchOpenTxDetails(state) {
            state.openTxDetails = !state.openTxDetails;
            saveToLs('openTxDetails', state.openTxDetails);
        },

        switchShowTxComments(state) {
            state.showTxComments = !state.showTxComments;
            saveToLs('showTxComments', state.showTxComments);
        },

        closeNewDefaultTxView(state) {
            state.newDefaultTxView = true;
            window.localStorage.setItem('newDefaultTxView', 'true');
        },

        toggleExcludeActionGroup(state, actionGroup) {
            if (state.excludeGroups.includes(actionGroup)) {
                const index = state.excludeGroups.indexOf(actionGroup);
                if (index > -1) { // only splice array when item is found
                    state.excludeGroups.splice(index, 1); // 2nd parameter means remove one item only
                }
            } else {
                state.excludeGroups.push(actionGroup);
            }
            saveToLs('excludeActionGroups', state.excludeGroups);
        },

        resetExcludeActionGroup(state) {
            state.excludeGroups = [];
            saveToLs('excludeActionGroups', state.excludeGroups);
        },
        disableAllExcludeActionGroup(state) {
            state.excludeGroups = Object.keys(state.actionGroups);
            saveToLs('excludeActionGroups', state.excludeGroups);
        },
        setHighlightedNode(state, name) {
            state.highlightedNodeName = name;
        },
        clearHighlightedNode(state) {
            state.highlightedNodeName = null;
        },
    },

    actions: {
        // Switches off everything that hides part of the address history (the date range has its own reset).
        // Only what's actually set is touched: every commit makes the history table reload.
        resetHistoryFilters({ state, getters, commit }, address) {
            if (state.excludeGroups.length > 0) commit('resetExcludeActionGroup');
            if (getters.initiatedByAccountFor(address)) commit('updateInitiatedByAccount', { address, value: false });
            if (state.transactionDirection !== 'all') commit('updateTransactionDirection', 'all');
            if (state.transactionViewData === 'professional') {
                commit('updateTransactionViewData', 'simple');
                window.localStorage.setItem('transactionViewData', 'simple');
            }
            if (state.showTxComments) commit('switchShowTxComments');
        },

        rememberRecentSearch({ commit }, suggestion) {
            commit('updateRecentSearch', { suggestion, forget: false });
        },

        forgetRecentSearch({ commit }, suggestion) {
            commit('updateRecentSearch', { suggestion, forget: true });
        },

        getAddrbook({ commit }) {
            return axios.get(ADDRBOOK_LOCATION).then(({ data }) => {
                commit('updateAddrbook', Object.freeze(data));
            });
        },

        getExchangeRates({ state, commit }) {
            const currency = state.exchangeRateCurrency;
            const [fallbackRate, fallbackTrend] = getFallbackExchangeRate(currency);

            // Something to show right away: the rate remembered from the last
            // visit, or the hardcoded one. The real rate replaces it as soon as
            // it lands, and it stays if the api never answers — otherwise every
            // fiat value on the site would silently disappear:
            commit('updateExchangeRate', { rate: fallbackRate, trend: fallbackTrend });

            return getExchangeRateAndTrend(currency).then(([rate, trend]) => {
                // The currency may have been switched again while this was in flight:
                if (state.exchangeRateCurrency !== currency) {
                    return;
                }

                commit('updateExchangeRate', { rate, trend });
                rememberExchangeRate(currency, rate, trend);
            }).catch((error) => {
                console.error('Error loading exchange rate:', error);
            });
        },

        rememberAddressbookItem({ commit }) {
            commit('addLocalAddressbook');
        },

        forgetAddressbookItem({ commit }, { address }) {
            commit('removeLocalAddressbook', { address });
        },

        editAddressbookItem({ commit }, { address, newName }) {
            commit('updateLocalAddressbook', { address, newName });
        },

        presaveAddressbookItem({ commit }, { address, name, type, image }) {
            commit('presaveLocalAddressbook', { address, name, type, image });
        },

        closeAd({ commit }) {
            commit('adCloseBannerAction');
        },
    },

    getters: {
        initiatedByAccountFor: state => (address) => {
            const raw = toRawAddress(address || '');
            return raw === state.initiatedByAccountAddress && state.initiatedByAccount;
        },
        historyFiltersApplied: (state, getters) => address => state.excludeGroups.length > 0
            || getters.initiatedByAccountFor(address)
            || state.transactionDirection !== 'all'
            // Not filters, but the raw «Developer» view and the comments column change what the list shows, so they count too:
            || state.transactionViewData === 'professional'
            || state.showTxComments,
        getAddressMeta: (state) => (address) => state.addrbook[address] || {}, // eslint-disable-line arrow-parens
        getAddressName: (state) => (address) => state.addrbook[address]?.name || address, // eslint-disable-line arrow-parens
        getLocalAddressbookName: state => (address) => {
            const name = state.appLocalAddressbook.filter(item => item.address === address)[0]?.name || null;

            return name > 0 ? null : name;
        },
        getLocalAddressbookObjectByName: state => (query) => { // eslint-disable-line arrow-body-style
            return state.appLocalAddressbook.filter(item => item.name.toString().toLowerCase().includes(query.toLowerCase())) || [];
        },
        getAdVisibility: (state) => {
            const now = Math.round(Date.now() / 1000);
            const till = state.closeAdTill;

            return now > till;
        },
    },
});
